/**
 * Shared, dependency-free formula library for the Black Friday toolkit.
 *
 * Pure functions only: inputs in, numbers out. No React, no state, no side
 * effects. Full precision is kept internally; rounding happens at display time
 * in the UI, never here.
 *
 * Several toolkit tools share this margin-arithmetic family, so every function
 * is unit-tested in isolation (see tests/unit/bf-toolkit-calculations.test.ts)
 * before any UI depends on it. The module grows as each tool lands.
 */

/**
 * Extra sales volume needed to hold gross profit constant after a discount.
 *
 * A discount cuts the margin on every unit, so you have to sell more units just
 * to end up with the same gross profit. This returns that required increase as
 * a percentage of current volume.
 *
 *   baseMargin      = price - cost                 (margin per unit today)
 *   discountedPrice = price * (1 - discountPct/100)
 *   newMargin       = discountedPrice - cost       (margin per unit after)
 *   uplift %        = (baseMargin / newMargin - 1) * 100
 *
 * Return contract (the UI reads these as distinct states):
 *  - `0` when there's no discount — nothing changes, no extra units needed.
 *  - `Infinity` when no volume can recover the profit: either the product is
 *    already sold at or below cost (baseMargin <= 0), or the discount drops the
 *    price to or below cost (newMargin <= 0).
 *  - `NaN` when any input isn't a finite number.
 *  - otherwise the required volume uplift as a percentage (e.g. 71.43).
 */
export function breakEvenVolumeUplift(
  price: number,
  cost: number,
  discountPct: number
): number {
  if (
    !Number.isFinite(price) ||
    !Number.isFinite(cost) ||
    !Number.isFinite(discountPct)
  ) {
    return Number.NaN;
  }

  // No discount means no change to margin, so no extra volume is required.
  if (discountPct <= 0) return 0;

  const baseMargin = price - cost;
  const discountedPrice = price * (1 - discountPct / 100);
  const newMargin = discountedPrice - cost;

  // No profit to protect (already at/below cost), or the discount pushes the
  // price to/below cost: no amount of extra volume holds gross profit.
  if (baseMargin <= 0 || newMargin <= 0) return Number.POSITIVE_INFINITY;

  return (baseMargin / newMargin - 1) * 100;
}

/** Result of {@link freeShippingThreshold}. All figures are full-precision. */
export interface FreeShippingThreshold {
  /**
   * The recommended basket-size threshold: the point at which the extra margin
   * from a topped-up basket exactly covers the shipping you'd absorb.
   * `Infinity` when no threshold can ever cover it (margin at/below zero),
   * `NaN` when an input isn't finite.
   */
  thresholdRaw: number;
  /**
   * How much more a customer must spend, on top of the current AOV, to reach
   * the threshold (`shippingCost / margin`). `Infinity` / `NaN` mirror
   * `thresholdRaw`.
   */
  extraSpendNeeded: number;
  /**
   * That extra spend as a percentage of the current AOV — the headline "baskets
   * only need to grow X%" figure. `0` when shipping is free, `Infinity` / `NaN`
   * mirror `thresholdRaw`.
   */
  upliftPct: number;
}

/**
 * The basket-size threshold at which offering free shipping pays for itself.
 *
 * Marginal view: a customer who tops their basket up from today's AOV to the
 * threshold spends `extra` more, earning you `extra * margin` in gross profit.
 * Set the threshold so that extra margin exactly covers the shipping cost you
 * now absorb:
 *
 *   extraSpendNeeded = shippingCost / (marginPct / 100)
 *   thresholdRaw     = currentAOV + extraSpendNeeded
 *   upliftPct        = extraSpendNeeded / currentAOV * 100
 *
 * `marginPct` is a whole-number percentage (e.g. 60 for 60%), matching the rest
 * of this module. Full precision is returned; the UI rounds the threshold up to
 * a clean number and recomputes the displayed uplift against it.
 *
 * Return contract (the UI reads these as distinct states):
 *  - shipping at/below zero → threshold is the AOV itself, `extraSpendNeeded`
 *    and `upliftPct` are `0` (free shipping costs nothing, offer it anywhere).
 *  - margin at/below zero → `Infinity` throughout: a topped-up basket earns no
 *    margin, so no threshold can ever pay for the shipping.
 *  - AOV at/below zero, or any non-finite input → `NaN` throughout.
 *  - otherwise the finite recommended figures.
 */
export function freeShippingThreshold(
  currentAOV: number,
  shippingCost: number,
  marginPct: number
): FreeShippingThreshold {
  const nan: FreeShippingThreshold = {
    thresholdRaw: Number.NaN,
    extraSpendNeeded: Number.NaN,
    upliftPct: Number.NaN,
  };

  if (
    !Number.isFinite(currentAOV) ||
    !Number.isFinite(shippingCost) ||
    !Number.isFinite(marginPct) ||
    currentAOV <= 0
  ) {
    return nan;
  }

  // Free (or negative) shipping cost: nothing to offset, so any basket size
  // works and no basket growth is required.
  if (shippingCost <= 0) {
    return { thresholdRaw: currentAOV, extraSpendNeeded: 0, upliftPct: 0 };
  }

  // No margin on the extra spend means it can never fund the shipping.
  if (marginPct <= 0) {
    return {
      thresholdRaw: Number.POSITIVE_INFINITY,
      extraSpendNeeded: Number.POSITIVE_INFINITY,
      upliftPct: Number.POSITIVE_INFINITY,
    };
  }

  const extraSpendNeeded = shippingCost / (marginPct / 100);
  return {
    thresholdRaw: currentAOV + extraSpendNeeded,
    extraSpendNeeded,
    upliftPct: (extraSpendNeeded / currentAOV) * 100,
  };
}

/** A single item in a bundle. Only price and cost drive the maths; any name a
 * user types is cosmetic and never reaches this layer. */
export interface BundleItem {
  price: number;
  cost: number;
}

/** Result of {@link blendedMargin}. All figures are full-precision. */
export interface BlendedMargin {
  /** Sum of every item's list price. */
  totalPrice: number;
  /** Sum of every item's unit cost. */
  totalCost: number;
  /** What the bundle actually sells for after the discount. */
  discountedRevenue: number;
  /** Gross profit on one bundle: `discountedRevenue - totalCost`. Goes negative
   * when the discount sells the bundle below cost. */
  marginPerBundle: number;
  /**
   * Blended gross margin as a percentage of discounted revenue
   * (`marginPerBundle / discountedRevenue * 100`). Negative when the bundle
   * sells below cost. `-Infinity` when the discount gives the bundle away
   * entirely (revenue at/below zero), `NaN` when there's nothing to price.
   */
  marginPct: number;
}

/** Total up a set of bundle items, ignoring any that aren't fully finite. */
function bundleTotals(items: BundleItem[]): {
  totalPrice: number;
  totalCost: number;
  allFinite: boolean;
} {
  let totalPrice = 0;
  let totalCost = 0;
  let allFinite = true;
  for (const item of items) {
    if (!Number.isFinite(item.price) || !Number.isFinite(item.cost)) {
      allFinite = false;
      continue;
    }
    totalPrice += item.price;
    totalCost += item.cost;
  }
  return { totalPrice, totalCost, allFinite };
}

/**
 * Blended gross margin across a set of items sold together at a bundle discount.
 *
 * The discount comes off the combined list price; cost is unchanged. Margin is
 * measured against the discounted revenue — the standard gross-margin
 * convention, matching how `marginPct` is used elsewhere in this module:
 *
 *   totalPrice        = Σ price
 *   totalCost         = Σ cost
 *   discountedRevenue = totalPrice * (1 - bundleDiscountPct/100)
 *   marginPerBundle   = discountedRevenue - totalCost
 *   marginPct         = marginPerBundle / discountedRevenue * 100
 *
 * Return contract (the UI reads these as distinct states):
 *  - No priceable bundle — empty list, every price non-finite, or a total list
 *    price at/below zero → all figures `NaN`.
 *  - Discount gives the bundle away (discounted revenue at/below zero, i.e. a
 *    100% discount) → `marginPct` is `-Infinity`; the money figures still
 *    reflect the full cost being lost.
 *  - A discount deep enough to sell below cost → a genuine negative
 *    `marginPerBundle` / `marginPct`, which the UI surfaces as a warning rather
 *    than a quiet minus sign.
 *  - otherwise the finite blended figures.
 */
export function blendedMargin(
  items: BundleItem[],
  bundleDiscountPct: number
): BlendedMargin {
  const nan: BlendedMargin = {
    totalPrice: Number.NaN,
    totalCost: Number.NaN,
    discountedRevenue: Number.NaN,
    marginPerBundle: Number.NaN,
    marginPct: Number.NaN,
  };

  if (!Number.isFinite(bundleDiscountPct) || items.length === 0) return nan;

  const { totalPrice, totalCost, allFinite } = bundleTotals(items);
  // Nothing sensible to price: no finite items got through, or the combined
  // list price is at/below zero.
  if (!allFinite && totalPrice === 0) return nan;
  if (totalPrice <= 0) return nan;

  const discountedRevenue = totalPrice * (1 - bundleDiscountPct / 100);
  const marginPerBundle = discountedRevenue - totalCost;

  // The whole bundle given away: revenue is zero (or negative), so margin as a
  // share of revenue is unbounded below.
  if (discountedRevenue <= 0) {
    return {
      totalPrice,
      totalCost,
      discountedRevenue,
      marginPerBundle,
      marginPct: Number.NEGATIVE_INFINITY,
    };
  }

  return {
    totalPrice,
    totalCost,
    discountedRevenue,
    marginPerBundle,
    marginPct: (marginPerBundle / discountedRevenue) * 100,
  };
}

/**
 * The deepest bundle discount that still holds a target blended margin.
 *
 * Inverse of {@link blendedMargin}: solve `marginPct = targetMarginPct` for the
 * discount. With `t = targetMarginPct/100`:
 *
 *   discountedRevenue must be   totalCost / (1 - t)
 *   so 1 - d = totalCost / (totalPrice * (1 - t))
 *   maxDiscountPct = (1 - totalCost / (totalPrice * (1 - t))) * 100
 *
 * The raw value is returned unclamped so the UI can tell the difference between
 * "plenty of room" and "already too thin":
 *  - `> 0` — the headline figure: discount up to here and still clear the target.
 *  - `<= 0` — the bundle's natural (undiscounted) margin is already at or below
 *    the target, so no discount is affordable; the more negative, the further
 *    under water. The UI shows a warning instead of a nonsensical negative
 *    discount.
 *  - `100` — cost is zero, so every pre-giveaway discount holds any target.
 *
 * Return contract:
 *  - No priceable bundle (see {@link blendedMargin}) → `NaN`.
 *  - `targetMarginPct` non-finite or ≥ 100 → `NaN`: a 100% margin needs zero
 *    cost, so it isn't a reachable target for a real bundle.
 *  - otherwise the (possibly non-positive) maximum discount percentage.
 */
export function maxDiscountForTargetMargin(
  items: BundleItem[],
  targetMarginPct: number
): number {
  if (
    !Number.isFinite(targetMarginPct) ||
    targetMarginPct >= 100 ||
    items.length === 0
  ) {
    return Number.NaN;
  }

  const { totalPrice, totalCost, allFinite } = bundleTotals(items);
  if (!allFinite && totalPrice === 0) return Number.NaN;
  if (totalPrice <= 0) return Number.NaN;

  // Zero (or negative) cost: the bundle is pure margin, so any discount short of
  // giving it away holds any target.
  if (totalCost <= 0) return 100;

  const t = targetMarginPct / 100;
  const requiredRevenue = totalCost / (1 - t);
  return (1 - requiredRevenue / totalPrice) * 100;
}

/**
 * Blended contribution-margin ratio left after an across-the-board discount.
 *
 * Retrospective companion to {@link breakEvenVolumeUplift}: instead of a single
 * product's price and cost, it works from the two figures a founder actually
 * has after a sale — their normal gross margin and the average discount depth
 * they ran. A unit that listed at 1 costs `(1 - m)` and, after the discount,
 * sells for `(1 - d)`, so the margin it earns is `(m - d)` on revenue `(1 - d)`:
 *
 *   ratio = (marginPct - discountPct) / (100 - discountPct)
 *
 * Both arguments are whole-number percentages (e.g. 55 for 55%), matching the
 * rest of this module. The result is a fraction of discounted revenue, so it
 * multiplies straight onto reported (already-discounted) sales.
 *
 * Return contract (the UI reads these as distinct states):
 *  - discount at/above 100% (the whole sale given away) → `-Infinity`: revenue
 *    is zero, so margin as a share of it is unbounded below.
 *  - margin below the discount → a genuine negative ratio (you sold below cost);
 *    returned as-is so the UI can surface the loss.
 *  - any non-finite input → `NaN`.
 *  - otherwise the (possibly small) contribution-margin ratio.
 */
export function postDiscountMarginRatio(
  marginPct: number,
  discountPct: number
): number {
  if (!Number.isFinite(marginPct) || !Number.isFinite(discountPct)) {
    return Number.NaN;
  }
  // The whole sale is given away: no revenue to measure margin against.
  if (discountPct >= 100) return Number.NEGATIVE_INFINITY;
  return (marginPct - discountPct) / (100 - discountPct);
}

/**
 * The blended break-even ROAS implied by a margin and discount depth.
 *
 * Break-even ROAS is `1 / contributionMarginRatio` — the return on ad spend at
 * which paid sales exactly cover their own variable costs (see
 * {@link postDiscountMarginRatio} for the ratio). Below it, every extra
 * ad-driven order loses money on a contribution basis; a common target is
 * 1.3–1.5× this figure to leave real profit after ads.
 *
 * Return contract (the UI reads these as distinct states):
 *  - margin at or below the discount → `Infinity`: post-discount contribution is
 *    zero or negative, so no finite ROAS can break even (more volume only loses
 *    more). Surfaced as "unattainable", not a nonsensical negative ROAS.
 *  - any non-finite input → `NaN`.
 *  - otherwise the positive break-even ROAS (e.g. 2.5 for a 40% contribution).
 */
export function breakEvenRoas(marginPct: number, discountPct: number): number {
  const ratio = postDiscountMarginRatio(marginPct, discountPct);
  // Only truly invalid inputs (via postDiscountMarginRatio) produce NaN here;
  // a 100% discount produces -Infinity, which the ratio<=0 guard below must
  // still reach so it can resolve to "unattainable" rather than NaN.
  if (Number.isNaN(ratio)) return Number.NaN;
  // No positive margin left on a sale: no amount of ROAS breaks even.
  if (ratio <= 0) return Number.POSITIVE_INFINITY;
  return 1 / ratio;
}

/**
 * Estimated contribution profit from a completed sale: what was left after
 * variable costs and ad spend, before fixed costs.
 *
 *   contribution = revenue * postDiscountMarginRatio(margin, discount) - adSpend
 *
 * `revenue` is the reported (already-discounted) sales total, so the ratio —
 * which is measured against discounted revenue — applies to it directly. The
 * result goes negative when the discount and ad spend together outrun the
 * margin, which is the headline the report card exists to surface.
 *
 * Returns `NaN` if any input is non-finite, or if the ratio itself is
 * non-finite (a 100%+ giveaway), so the UI can fall back to a neutral state.
 */
export function contributionProfit(
  revenue: number,
  marginPct: number,
  discountPct: number,
  adSpend: number
): number {
  const ratio = postDiscountMarginRatio(marginPct, discountPct);
  if (
    !Number.isFinite(revenue) ||
    !Number.isFinite(adSpend) ||
    !Number.isFinite(ratio)
  ) {
    return Number.NaN;
  }
  return revenue * ratio - adSpend;
}

/**
 * Inverse of the standard normal CDF (the probit / quantile function).
 *
 * Peter Acklam's rational approximation: a closed form, not an iterative solver,
 * accurate to ~1e-9 across the open interval (0, 1). Used to turn a critical
 * ratio into the z-score that sets the newsvendor order quantity.
 *
 * Returns `-Infinity`/`Infinity` at the 0/1 boundaries and `NaN` outside them.
 */
function inverseNormalCdf(p: number): number {
  if (!Number.isFinite(p) || p < 0 || p > 1) return Number.NaN;
  if (p === 0) return Number.NEGATIVE_INFINITY;
  if (p === 1) return Number.POSITIVE_INFINITY;

  const a = [
    -3.969683028665376e1, 2.209460984245205e2, -2.759285104469687e2,
    1.38357751867269e2, -3.066479806614716e1, 2.506628277459239,
  ] as const;
  const b = [
    -5.447609879822406e1, 1.615858368580409e2, -1.556989798598866e2,
    6.680131188771972e1, -1.328068155288572e1,
  ] as const;
  const c = [
    -7.784894002430293e-3, -3.223964580411365e-1, -2.400758277161838,
    -2.549732539343734, 4.374664141464968, 2.938163982698783,
  ] as const;
  const d = [
    7.784695709041462e-3, 3.224671290700398e-1, 2.445134137142996,
    3.754408661907416,
  ] as const;

  const pLow = 0.02425;
  const pHigh = 1 - pLow;

  if (p < pLow) {
    const q = Math.sqrt(-2 * Math.log(p));
    return (
      (((((c[0] * q + c[1]) * q + c[2]) * q + c[3]) * q + c[4]) * q + c[5]) /
      ((((d[0] * q + d[1]) * q + d[2]) * q + d[3]) * q + 1)
    );
  }
  if (p <= pHigh) {
    const q = p - 0.5;
    const r = q * q;
    return (
      ((((((a[0] * r + a[1]) * r + a[2]) * r + a[3]) * r + a[4]) * r + a[5]) *
        q) /
      (((((b[0] * r + b[1]) * r + b[2]) * r + b[3]) * r + b[4]) * r + 1)
    );
  }
  const q = Math.sqrt(-2 * Math.log(1 - p));
  return (
    -(((((c[0] * q + c[1]) * q + c[2]) * q + c[3]) * q + c[4]) * q + c[5]) /
    ((((d[0] * q + d[1]) * q + d[2]) * q + d[3]) * q + 1)
  );
}

/** How spread out demand is, driving the coefficient of variation used to size
 * the order. Chosen by a plain-language confidence chip in the UI, not a raw σ. */
export type DemandSpread = "tight" | "wide";

/**
 * Coefficient of variation (σ as a fraction of expected demand) for each
 * confidence level. Modeling assumptions — deliberately round, not derived from
 * any Wayflyer data — that turn "fairly sure" / "could be off a lot" into a
 * demand spread. Tune here in one place; the tests pin these values.
 */
export const DEMAND_SPREAD_CV: Record<DemandSpread, number> = {
  tight: 0.15,
  wide: 0.35,
};

/** Result of {@link newsvendorOrderQty}. */
export interface OrderQuantity {
  /**
   * - `"ok"` — a normal recommendation from the critical ratio.
   * - `"no-margin"` — price is at or below cost, so there's no profit to chase
   *   by stocking; `recommendedOrder` is `0`.
   * - `"overstock-free"` — leftovers are worth at least their cost, so there's
   *   no downside to overstocking; `recommendedOrder` covers strong demand.
   */
  status: "ok" | "no-margin" | "overstock-free";
  /** The critical ratio `Cu / (Cu + Co)`; `NaN` when it doesn't apply. */
  criticalRatio: number;
  /**
   * The order quantity to place, always a whole number of units. Without an
   * `orderMultiple` this is the optimum rounded UP to the next unit (never
   * under-order on a rounding). With an `orderMultiple` it's the multiple that
   * earns the most expected profit — which can sit just below the optimum, so
   * it is not always rounded up. `NaN` when inputs are unusable.
   */
  recommendedOrder: number;
  /**
   * The optimum before any order-multiple constraint, rounded up to a whole
   * unit. Equals `recommendedOrder` when no multiple applies; when one does,
   * it's the figure the multiple was fitted to. `NaN` when inputs are unusable.
   */
  unconstrainedOrder: number;
  /** Weaker-demand scenario: `round(demand - σ)`, floored at 0. */
  demandLow: number;
  /** Expected-demand scenario: `round(demand)`. */
  demandExpected: number;
  /** Stronger-demand scenario: `round(demand + σ)`. */
  demandHigh: number;
}

/** Error function via Abramowitz & Stegun 7.1.26 (max error ~1.5e-7). */
function erf(x: number): number {
  const sign = x < 0 ? -1 : 1;
  const ax = Math.abs(x);
  const t = 1 / (1 + 0.3275911 * ax);
  const y =
    1 -
    ((((1.061405429 * t - 1.453152027) * t + 1.421413741) * t - 0.284496736) *
      t +
      0.254829592) *
      t *
      Math.exp(-ax * ax);
  return sign * y;
}

/** Standard normal CDF, Φ(z). */
function normalCdf(z: number): number {
  return 0.5 * (1 + erf(z / Math.SQRT2));
}

/** Standard normal PDF, φ(z). */
function normalPdf(z: number): number {
  return Math.exp(-0.5 * z * z) / Math.sqrt(2 * Math.PI);
}

/**
 * Expected profit from ordering `order` units for a one-shot window, with demand
 * modelled as normal around `expectedDemand` (σ from the confidence spread).
 *
 *   z               = (order - μ) / σ
 *   expected sales  = μ - σ·L(z)         L(z) = φ(z) - z·(1 - Φ(z))  (lost sales)
 *   expected left   = (order - μ)·Φ(z) + σ·φ(z)
 *   profit          = price·sales - cost·order + salvage·left
 *
 * This is what {@link newsvendorOrderQty} maximises. Returns `NaN` for unusable
 * inputs (non-finite, `expectedDemand <= 0`, or a negative `order`).
 */
export function expectedNewsvendorProfit(
  order: number,
  expectedDemand: number,
  demandSpread: DemandSpread,
  unitCost: number,
  unitPrice: number,
  salvageValue: number
): number {
  const cv = DEMAND_SPREAD_CV[demandSpread];
  if (
    !Number.isFinite(order) ||
    !Number.isFinite(expectedDemand) ||
    !Number.isFinite(unitCost) ||
    !Number.isFinite(unitPrice) ||
    !Number.isFinite(salvageValue) ||
    !Number.isFinite(cv) ||
    expectedDemand <= 0 ||
    order < 0
  ) {
    return Number.NaN;
  }

  const sigma = cv * expectedDemand;
  const z = (order - expectedDemand) / sigma;
  // Clamp against tiny negative values from floating-point error.
  const expectedShortage = Math.max(
    sigma * (normalPdf(z) - z * (1 - normalCdf(z))),
    0
  );
  const expectedLeftover = Math.max(
    (order - expectedDemand) * normalCdf(z) + sigma * normalPdf(z),
    0
  );
  const expectedSales = expectedDemand - expectedShortage;
  return (
    unitPrice * expectedSales -
    unitCost * order +
    salvageValue * expectedLeftover
  );
}

/**
 * The classic newsvendor order quantity: how many units to buy for a one-shot
 * selling window when demand is uncertain, balancing the cost of running out
 * against the cost of being left with stock.
 *
 *   Cu (underage) = unitPrice - unitCost      profit lost on a unit you run short
 *   Co (overage)  = unitCost - salvageValue   loss on a unit left unsold
 *   critical ratio = Cu / (Cu + Co)
 *   σ  = coefficient of variation × expectedDemand   (spread from the chip)
 *   Q* = ceil(expectedDemand + z(critical ratio) · σ)
 *
 * where `z` is the inverse standard-normal CDF; demand is modelled as normal
 * around the expectation. Three demand scenarios (weaker / expected / stronger)
 * are returned alongside so the UI can show how the single order holds up.
 *
 * When `orderMultiple` is supplied, the recommendation is the multiple of it
 * that earns the most expected profit (see {@link expectedNewsvendorProfit}).
 * The profit curve is concave, so the best multiple is whichever of the two
 * straddling the optimum wins on profit — and because overshooting past the
 * peak can cost more than falling just short, that may round the optimum DOWN,
 * not up.
 *
 * Return contract (statuses documented on {@link OrderQuantity}):
 *  - Non-finite input, or `expectedDemand <= 0` → every figure `NaN`.
 *  - `unitPrice <= unitCost` → `no-margin`, `recommendedOrder` 0 (scenarios
 *    still populated).
 *  - `salvageValue >= unitCost` → `overstock-free`, `recommendedOrder` covers
 *    strong demand (rounded UP to any order multiple, since more never hurts).
 *  - otherwise `ok` with the profit-maximising order quantity.
 */
export function newsvendorOrderQty(
  expectedDemand: number,
  demandSpread: DemandSpread,
  unitCost: number,
  unitPrice: number,
  salvageValue: number,
  orderMultiple?: number
): OrderQuantity {
  const cv = DEMAND_SPREAD_CV[demandSpread];

  if (
    !Number.isFinite(expectedDemand) ||
    !Number.isFinite(unitCost) ||
    !Number.isFinite(unitPrice) ||
    !Number.isFinite(salvageValue) ||
    !Number.isFinite(cv) ||
    expectedDemand <= 0
  ) {
    return {
      status: "no-margin",
      criticalRatio: Number.NaN,
      recommendedOrder: Number.NaN,
      unconstrainedOrder: Number.NaN,
      demandLow: Number.NaN,
      demandExpected: Number.NaN,
      demandHigh: Number.NaN,
    };
  }

  const sigma = cv * expectedDemand;
  const demandLow = Math.max(Math.round(expectedDemand - sigma), 0);
  const demandExpected = Math.round(expectedDemand);
  const demandHigh = Math.round(expectedDemand + sigma);

  // A usable order multiple (a whole unit, >= 1), or 0 when none applies.
  const step =
    orderMultiple !== undefined &&
    Number.isInteger(orderMultiple) &&
    orderMultiple >= 1
      ? orderMultiple
      : 0;

  const underage = unitPrice - unitCost;
  const overage = unitCost - salvageValue;

  // No profit per unit: stocking more can't help, so there's nothing to order.
  if (underage <= 0) {
    return {
      status: "no-margin",
      criticalRatio: Number.NaN,
      recommendedOrder: 0,
      unconstrainedOrder: 0,
      demandLow,
      demandExpected,
      demandHigh,
    };
  }

  // Leftovers recover at least their cost: overstocking carries no penalty, so
  // stock for strong demand rather than solving a (degenerate) critical ratio.
  // More is always fine here, so any order multiple rounds UP.
  if (overage <= 0) {
    return {
      status: "overstock-free",
      criticalRatio: 1,
      recommendedOrder:
        step >= 1 ? Math.ceil(demandHigh / step) * step : demandHigh,
      unconstrainedOrder: demandHigh,
      demandLow,
      demandExpected,
      demandHigh,
    };
  }

  const criticalRatio = underage / (underage + overage);
  const z = inverseNormalCdf(criticalRatio);
  const qStar = expectedDemand + z * sigma;
  // The unconstrained recommendation rounds UP to a whole unit: you can't buy a
  // fraction, and under-ordering forgoes margin.
  const unconstrainedOrder = Math.max(Math.ceil(qStar), 0);

  let recommendedOrder = unconstrainedOrder;
  if (step >= 1) {
    // Expected profit is concave in the order, so the best feasible multiple is
    // one of the two straddling the optimum. Compare their expected profit and
    // take the higher — overshooting the peak can cost more than falling just
    // short, so this may round DOWN.
    const lower = Math.max(Math.floor(qStar / step) * step, 0);
    const upper = lower + step;
    const profitLower = expectedNewsvendorProfit(
      lower,
      expectedDemand,
      demandSpread,
      unitCost,
      unitPrice,
      salvageValue
    );
    const profitUpper = expectedNewsvendorProfit(
      upper,
      expectedDemand,
      demandSpread,
      unitCost,
      unitPrice,
      salvageValue
    );
    recommendedOrder = profitUpper >= profitLower ? upper : lower;
  }

  return {
    status: "ok",
    criticalRatio,
    recommendedOrder,
    unconstrainedOrder,
    demandLow,
    demandExpected,
    demandHigh,
  };
}

/** The three discount percentages a clearance ladder steps through. Deliberately
 * round, editable defaults (not derived from any Wayflyer data) — the merchant
 * can override any of them in the UI. */
export interface DiscountTiers {
  /** Discount in effect from today. */
  start: number;
  /** Discount stepped up to at the first checkpoint, if still behind pace. */
  mid: number;
  /** Discount for the final push, if still behind pace at the last checkpoint. */
  final: number;
}

/** Default {@link DiscountTiers}, matching the toolkit spec's own worked example. */
export const DEFAULT_CLEARANCE_TIERS: DiscountTiers = {
  start: 20,
  mid: 30,
  final: 50,
};

/**
 * How strongly weekly sales respond to a markdown, as a price elasticity of
 * demand (magnitude). Deliberately round, user-owned assumptions — chosen by a
 * plain-language chip in the UI, never derived from Wayflyer data — that map
 * "how price-sensitive are your customers" to the exponent in the
 * constant-elasticity velocity model (see {@link velocityAtDiscount}). Tune in
 * one place; the tests pin these values.
 */
export type PriceSensitivity = "cautious" | "average" | "deal-driven";

export const PRICE_SENSITIVITY_ELASTICITY: Record<PriceSensitivity, number> = {
  cautious: 0.8,
  average: 1.5,
  "deal-driven": 2.5,
};

/** Bounds for the advanced elasticity slider, so a hand-set value can't drive
 * the model to nonsensical extremes. */
export const ELASTICITY_RANGE = { min: 0, max: 4 } as const;

/**
 * Weekly sell-through at a given discount, under a constant-elasticity demand
 * model.
 *
 *   velocity = baseVelocity × (1 − discount)^(−elasticity)
 *
 * A deeper discount lifts velocity; a higher elasticity makes that lift
 * steeper. `elasticity` of 0 means demand doesn't respond at all (velocity
 * unchanged). The discount is clamped below 100% so the multiplier stays
 * finite even at a full giveaway — real depletion is capped by the units on
 * hand anyway. Returns `NaN` for non-finite inputs or a negative base velocity.
 */
export function velocityAtDiscount(
  baseVelocity: number,
  discountPct: number,
  elasticity: number
): number {
  if (
    !Number.isFinite(baseVelocity) ||
    !Number.isFinite(discountPct) ||
    !Number.isFinite(elasticity) ||
    baseVelocity < 0
  ) {
    return Number.NaN;
  }
  const d = Math.min(Math.max(discountPct, 0), 99) / 100;
  return baseVelocity * (1 - d) ** -Math.max(elasticity, 0);
}

/** One phase of a {@link clearanceLadder} schedule: a stretch of the runway at
 * a single discount, starting where the discount changed. */
export interface ClearancePhase {
  /** Which tier this phase runs at. */
  tier: "start" | "mid" | "final";
  /** Weeks from today this phase begins (may be fractional — the UI resolves
   * it to a calendar date; rounding happens there, never here). */
  weekStart: number;
  discountPct: number;
  /** Projected weekly sell-through at this discount, from the velocity model. */
  velocityPerWeek: number;
  /** That velocity as a percentage lift over the current pace. */
  velocityUpliftPct: number;
  /** Selling price after the discount. */
  pricePerUnit: number;
  /** Gross margin per unit at this discount: `pricePerUnit − unitCost`. Goes
   * negative when the discount sells below cost. */
  marginPerUnit: number;
  /** True when `marginPerUnit` is below zero — surfaced as a visible warning,
   * not a quiet minus sign. */
  belowCost: boolean;
  /** Units projected still unsold when this phase begins. */
  unitsAtPhaseStart: number;
}

/** A point on a depletion curve: units still unsold at a given week. */
export interface DepletionPoint {
  week: number;
  units: number;
}

/** Result of {@link clearanceLadder}. */
export interface ClearanceLadderResult {
  /**
   * - `"ok"` — a normal schedule, in `phases`.
   * - `"already-cleared"` — `unitsRemaining` at/below zero: nothing to clear.
   * - `"past-target"` — `weeksToTarget` at/below zero: no runway left.
   * - `"invalid"` — a non-finite input.
   */
  status: "ok" | "already-cleared" | "past-target" | "invalid";
  /** The markdown phases, in order. One per discount change, so 1–3 entries.
   * Empty unless `status` is `"ok"`. */
  phases: ClearancePhase[];
  /** Weekly units-remaining curve under the laddered schedule, from week 0 to
   * the target (with a final point exactly at the target). */
  curve: DepletionPoint[];
  /** The same curve if the merchant does nothing — sells on at the current
   * pace and price. The visual contrast the tool is built around. */
  baselineCurve: DepletionPoint[];
  /** Whether the laddered schedule clears the stock by the target date. */
  clearsByTarget: boolean;
  /** Units still unsold at the target under the ladder (0 when it clears). */
  unitsAtTarget: number;
  /** Units still unsold at the target if the merchant does nothing. */
  baselineUnitsAtTarget: number;
  /** Units the ladder clears by the target (`unitsRemaining − unitsAtTarget`). */
  unitsCleared: number;
  /** Units the do-nothing path clears by the target. */
  baselineUnitsCleared: number;
  /** Week the ladder clears the stock, or `null` if it doesn't within the
   * runway. */
  clearWeek: number | null;
  /** Whether the current full-price pace already clears the stock by the
   * target. When true, a markdown only sacrifices margin — the UI leads with
   * "no markdown needed" rather than opening on a discount. */
  baselineClears: boolean;
  /** Week the full-price pace clears the stock, or `null` if it never does
   * (zero pace) — independent of the target date. */
  baselineClearWeek: number | null;
  /** Gross margin the laddered schedule captures over the runway, summed over
   * every unit at the discount live when it sells. Often *lower* than the
   * baseline: the ladder gives up margin per unit to clear stock faster. */
  marginCaptured: number;
  /** Gross margin the do-nothing path captures — every unit it sells goes at
   * full price, so this can exceed `marginCaptured` even though it clears far
   * less stock. The honest other side of the trade-off. */
  baselineMarginCaptured: number;
  /** Total revenue given up to the discount under the ladder: for every unit
   * sold, the gap between its full price and its discounted price. Zero when
   * no reached step actually discounts. The do-nothing path's discount cost is
   * always zero (it never discounts). */
  discountCost: number;
  /** Estimated storage cost accrued over the runway under the ladder, or
   * `null` when no holding-cost rate was supplied. */
  holdingCostTotal: number | null;
  /** Estimated storage cost over the runway if the merchant does nothing (more
   * stock held for longer, so usually higher). `null` when no rate supplied. */
  baselineHoldingCostTotal: number | null;
  /** How many times the schedule escalated a tier because it was behind pace
   * (0–2). Exposed mainly for analytics. */
  escalations: number;
}

/**
 * A markdown schedule for clearing leftover Black Friday stock by a target
 * date, modelling how each discount lifts weekly sell-through so it can show
 * whether the plan actually clears the stock in time and what it costs in
 * margin.
 *
 * The runway is split into three equal phases. The schedule starts at
 * `tiers.start` and, at each of the two phase boundaries, steps up to the next
 * tier **only if still behind a straight-line pace to zero, and only if that
 * tier is deeper than the discount already running** — a ladder never steps to
 * a shallower price, so a step set at or below the current discount is skipped
 * (and greys out in the UI). A merchant already clearing fast enough is never
 * pushed into a deeper discount than they need. Velocity at each tier comes
 * from {@link velocityAtDiscount} using the supplied `elasticity`; depletion is
 * simulated across the runway (capped at the units on hand), and a do-nothing
 * baseline is returned alongside for contrast.
 *
 * `elasticity` is a user-owned assumption (see {@link PriceSensitivity}), not a
 * Wayflyer statistic. Below-cost tiers are honoured and flagged, never capped —
 * clearing dead stock at a loss can beat holding it.
 *
 * Return contract (statuses documented on {@link ClearanceLadderResult}):
 *  - Non-finite input (including any tier) → `"invalid"`.
 *  - `unitsRemaining <= 0` → `"already-cleared"`.
 *  - `weeksToTarget <= 0` → `"past-target"`.
 *  - otherwise `"ok"`.
 */
export function clearanceLadder(
  unitsRemaining: number,
  unitCost: number,
  sellingPrice: number,
  weeksToTarget: number,
  currentSellThroughPerWeek: number,
  elasticity: number,
  tiers: DiscountTiers = DEFAULT_CLEARANCE_TIERS,
  holdingCostPerUnitPerWeek?: number
): ClearanceLadderResult {
  const empty = (
    status: ClearanceLadderResult["status"]
  ): ClearanceLadderResult => ({
    status,
    phases: [],
    curve: [],
    baselineCurve: [],
    clearsByTarget: false,
    unitsAtTarget: Math.max(unitsRemaining, 0) || 0,
    baselineUnitsAtTarget: Math.max(unitsRemaining, 0) || 0,
    unitsCleared: 0,
    baselineUnitsCleared: 0,
    clearWeek: null,
    baselineClears: false,
    baselineClearWeek: null,
    marginCaptured: 0,
    baselineMarginCaptured: 0,
    discountCost: 0,
    holdingCostTotal: null,
    baselineHoldingCostTotal: null,
    escalations: 0,
  });

  if (
    !Number.isFinite(unitsRemaining) ||
    !Number.isFinite(unitCost) ||
    !Number.isFinite(sellingPrice) ||
    !Number.isFinite(weeksToTarget) ||
    !Number.isFinite(currentSellThroughPerWeek) ||
    !Number.isFinite(elasticity) ||
    !Number.isFinite(tiers.start) ||
    !Number.isFinite(tiers.mid) ||
    !Number.isFinite(tiers.final)
  ) {
    return empty("invalid");
  }

  if (unitsRemaining <= 0) return empty("already-cleared");
  if (weeksToTarget <= 0) return empty("past-target");

  // A reported negative pace isn't meaningful; treat it as no sales.
  const basePace = Math.max(currentSellThroughPerWeek, 0);
  const holdingRate =
    holdingCostPerUnitPerWeek !== undefined &&
    Number.isFinite(holdingCostPerUnitPerWeek)
      ? Math.max(holdingCostPerUnitPerWeek, 0)
      : null;

  const tierOrder = ["start", "mid", "final"] as const;
  const tierDiscount = (t: (typeof tierOrder)[number]) => tiers[t];
  const b1 = weeksToTarget / 3;
  const b2 = (2 * weeksToTarget) / 3;
  // Straight-line units-remaining target at week t: the pace that would clear
  // exactly at the target. Being above it means behind pace.
  const straightLine = (t: number) =>
    Math.max(unitsRemaining * (1 - t / weeksToTarget), 0);

  const phaseAt = (
    tier: (typeof tierOrder)[number],
    weekStart: number,
    unitsAtPhaseStart: number
  ): ClearancePhase => {
    const discountPct = tierDiscount(tier);
    const velocityPerWeek = velocityAtDiscount(
      basePace,
      discountPct,
      elasticity
    );
    const pricePerUnit = sellingPrice * (1 - discountPct / 100);
    const marginPerUnit = pricePerUnit - unitCost;
    return {
      tier,
      weekStart,
      discountPct,
      velocityPerWeek,
      velocityUpliftPct:
        basePace > 0 ? (velocityPerWeek / basePace - 1) * 100 : 0,
      pricePerUnit,
      marginPerUnit,
      belowCost: marginPerUnit < 0,
      unitsAtPhaseStart,
    };
  };

  // Simulate depletion daily for a smooth curve, escalating the tier at each
  // phase boundary when still behind pace. Whole-day steps keep the maths
  // stable; curve points are emitted weekly plus one exactly at the target.
  const totalDays = Math.max(Math.round(weeksToTarget * 7), 1);
  const phases: ClearancePhase[] = [phaseAt("start", 0, unitsRemaining)];
  let escalations = 0;
  let checkedB1 = false;
  let checkedB2 = false;
  let units = unitsRemaining;
  let marginCaptured = 0;
  let discountCost = 0;
  let holdingAccrued = 0;
  let baselineHoldingAccrued = 0;

  const curve: DepletionPoint[] = [{ week: 0, units: unitsRemaining }];

  for (let day = 1; day <= totalDays; day++) {
    const t = (day / totalDays) * weeksToTarget;

    // Escalation decisions happen once, as each boundary is crossed. A step is
    // only taken when behind pace AND the candidate discount is deeper than the
    // one currently running — a ladder never steps to a shallower price, so a
    // step configured at or below the current discount is simply skipped (and
    // greys out in the UI).
    const maybeEscalate = (candidate: "mid" | "final") => {
      const current = phases[phases.length - 1] as ClearancePhase;
      if (
        units > straightLine(t) &&
        tierDiscount(candidate) > current.discountPct
      ) {
        escalations += 1;
        phases.push(phaseAt(candidate, t, units));
      }
    };
    if (!checkedB1 && t >= b1) {
      checkedB1 = true;
      maybeEscalate("mid");
    }
    if (!checkedB2 && t >= b2) {
      checkedB2 = true;
      maybeEscalate("final");
    }

    const phase = phases[phases.length - 1] as ClearancePhase;
    const dtWeeks = weeksToTarget / totalDays;
    const sold = Math.min(units, phase.velocityPerWeek * dtWeeks);
    units = Math.max(units - sold, 0);
    marginCaptured += sold * phase.marginPerUnit;
    // Revenue given up to the discount: full price minus the discounted price,
    // on every unit sold at that discount.
    discountCost += sold * (sellingPrice - phase.pricePerUnit);
    if (holdingRate !== null) {
      holdingAccrued += units * holdingRate * dtWeeks;
      // The same accrual for the do-nothing path, so the comparison is
      // like-for-like: units held at the current pace and full price.
      const baseUnits = Math.max(unitsRemaining - basePace * t, 0);
      baselineHoldingAccrued += baseUnits * holdingRate * dtWeeks;
    }

    // Emit a weekly sample (and always the final day at the target).
    const weeksElapsed = t;
    const isWeekBoundary =
      Math.abs(weeksElapsed - Math.round(weeksElapsed)) < dtWeeks / 2;
    if (isWeekBoundary || day === totalDays) {
      curve.push({ week: weeksElapsed, units });
    }
  }

  // Do-nothing baseline: sell on at the current pace and price for the runway.
  const baselineCurve: DepletionPoint[] = [];
  const baseSteps = Math.max(Math.ceil(weeksToTarget), 1);
  for (let w = 0; w <= baseSteps; w++) {
    const week = Math.min(w, weeksToTarget);
    baselineCurve.push({
      week,
      units: Math.max(unitsRemaining - basePace * week, 0),
    });
  }
  const baselineUnitsAtTarget = Math.max(
    unitsRemaining - basePace * weeksToTarget,
    0
  );

  const clearWeekPoint = curve.find((p) => p.units <= 1e-6);
  const clearWeek = clearWeekPoint ? clearWeekPoint.week : null;

  // When the current full-price pace already clears the stock by the target,
  // a markdown only gives away margin for no gain — the tool should say so
  // rather than open on a discount. `baselineClearWeek` is when full price
  // hits zero.
  const baselineClearWeek =
    basePace > 0 ? unitsRemaining / basePace : Number.POSITIVE_INFINITY;
  const baselineClears = baselineClearWeek <= weeksToTarget + 1e-6;

  // Do-nothing sells only at full price, so every unit it clears keeps the full
  // gross margin. That's exactly why it can out-earn the ladder on margin while
  // leaving far more stock (and cash) stuck — the comparison the UI surfaces.
  const unitsCleared = unitsRemaining - units;
  const baselineUnitsCleared = unitsRemaining - baselineUnitsAtTarget;
  const baselineMarginCaptured =
    baselineUnitsCleared * (sellingPrice - unitCost);

  return {
    status: "ok",
    phases,
    curve,
    baselineCurve,
    clearsByTarget: units <= 1e-6,
    unitsAtTarget: units,
    baselineUnitsAtTarget,
    unitsCleared,
    baselineUnitsCleared,
    clearWeek,
    baselineClears,
    baselineClearWeek: Number.isFinite(baselineClearWeek)
      ? baselineClearWeek
      : null,
    marginCaptured,
    baselineMarginCaptured,
    discountCost,
    holdingCostTotal: holdingRate === null ? null : holdingAccrued,
    baselineHoldingCostTotal:
      holdingRate === null ? null : baselineHoldingAccrued,
    escalations,
  };
}

/**
 * The share of financed inventory you must sell for the financing fee to pay
 * for itself.
 *
 * Financing an amount `X` of inventory (bought at unit cost `c`) buys `X / c`
 * units. Each sold unit returns its margin `m = p · GM`, where `GM` is the
 * gross-margin fraction. The financing costs `X · f` — a flat fee `f` on the
 * amount financed. Setting the gross profit from the units sold equal to that
 * fee and solving for the sold fraction:
 *
 *   s* = f · (1 − GM) / GM
 *
 * The result turns only on the fee and the margin — it is independent of how
 * much is financed. `marginPct` and `feePct` are whole-number percentages
 * (60 for 60%, 5 for 5%), matching the rest of this module.
 *
 * Return contract (the UI reads these as distinct states):
 *  - `0` when the fee is at/below zero — nothing to recover, always worth it.
 *  - a value `> 1` when even a full sell-through can't cover the fee (a thin
 *    margin against a steep fee); the UI surfaces this as "doesn't pay off".
 *  - `Infinity` when there's no margin to earn on (`GM` at/below zero).
 *  - `NaN` when any input isn't finite, or the margin is at/above 100%.
 *  - otherwise the required sell-through as a fraction (e.g. `0.05` for 5%).
 */
export function financingBreakEvenSellThrough(
  marginPct: number,
  feePct: number
): number {
  if (!Number.isFinite(marginPct) || !Number.isFinite(feePct)) {
    return Number.NaN;
  }
  // A margin of 100% or more is not a real gross margin (cost at/below zero).
  if (marginPct >= 100) return Number.NaN;
  // No fee to recover: financing it is trivially worth it.
  if (feePct <= 0) return 0;

  const gm = marginPct / 100;
  // No margin to earn on, so no amount of sell-through recovers the fee.
  if (gm <= 0) return Number.POSITIVE_INFINITY;

  const f = feePct / 100;
  return (f * (1 - gm)) / gm;
}

/** Result of {@link financingOutcome}. All figures are full-precision. */
export interface FinancingOutcome {
  /** Break-even sell-through as a fraction — see
   * {@link financingBreakEvenSellThrough} for the full return contract. */
  breakEvenSellThrough: number;
  /** The flat financing fee in money: `financedAmount · feePct/100`. */
  feeAmount: number;
  /**
   * Gross profit earned on the financed stock sold at `sellThroughPct`, net of
   * the fee: `e · financedAmount · GM/(1−GM) − feeAmount`, where `e` is the
   * sell-through fraction. `NaN` mirrors an invalid break-even.
   */
  profitAtSellThrough: number;
  /** `true` when even a full sell-through can't cover the fee
   * (`breakEvenSellThrough > 1`). */
  unrecoverable: boolean;
}

/**
 * The money view of financing a given amount of Black Friday stock: the fee it
 * costs, the break-even sell-through, and the profit it throws off at an
 * expected sell-through.
 *
 * Percentages are whole numbers (60 for 60%). `financedAmount` is the money
 * put on financing; a non-finite or negative amount is treated as `0`. Full
 * precision is returned; the UI rounds at display time.
 */
export function financingOutcome({
  marginPct,
  feePct,
  financedAmount,
  sellThroughPct,
}: {
  marginPct: number;
  feePct: number;
  financedAmount: number;
  sellThroughPct: number;
}): FinancingOutcome {
  const breakEvenSellThrough = financingBreakEvenSellThrough(marginPct, feePct);
  const financed =
    Number.isFinite(financedAmount) && financedAmount > 0 ? financedAmount : 0;
  const f = Number.isFinite(feePct) ? Math.max(feePct, 0) / 100 : 0;
  const feeAmount = financed * f;

  const gm = marginPct / 100;
  const e =
    Number.isFinite(sellThroughPct) && sellThroughPct > 0
      ? sellThroughPct / 100
      : 0;

  // Margin earned per unit of financed cost is GM/(1−GM); below that guard the
  // ratio is undefined, so profit isn't meaningful. Also require a valid
  // break-even (mirrors financingBreakEvenSellThrough's own guards) so a bad
  // feePct can't slip through as a finite profit.
  const profitAtSellThrough =
    Number.isFinite(breakEvenSellThrough) && gm > 0 && gm < 1
      ? e * financed * (gm / (1 - gm)) - feeAmount
      : Number.NaN;

  return {
    breakEvenSellThrough,
    feeAmount,
    profitAtSellThrough,
    // `> 1` is false for NaN (an invalid input, not a real break-even) and
    // true for +Infinity (zero/negative margin: never recoverable), so no
    // separate finiteness check is needed here.
    unrecoverable: breakEvenSellThrough > 1,
  };
}
